/**
 * Utility functions for branching based on device type or feature. They are localized here so it's
 * easier to customize them later. Using solely mobile detect is very unprecise.
 */
import { UAParser } from 'ua-parser-js';

import { setCameraOptions } from 'inquiry/redux/actions/ui';
import { createCanvas } from 'lib/utils/canvas';
import { VerifyThunkAction } from 'verify/redux/types';
import { getCameraDevices } from 'verify/scanners/GovernmentIdMicroblinkScanner/MicroblinkCameraUtils';

/**
 * Tapping <input type='file' capture ... /> on mobile will open the native camera view.
 * If unsupported, will open a regular file browser.
 *
 * We use this as a heuristic to check if the user's device is a phone or tablet.
 * NOTE: convertible laptops like Microsoft Surface do not support input capture.
 *
 * TODO: [PER-1757] Check if there is a better way to check if the device is mobile.
 */
export function supportsInputCapture() {
  // Override mock for cypress
  // NOTE: We need a try/catch because accessing window in a cross-origin
  // context will raise a security error.
  try {
    if (window.cypressInputCapture || window.parent.window.cypressInputCapture) return true;
  } catch {
    // Do nothing
    // DOMException: Permission denied to access property "cypressInputCapture" on cross-origin object
  }

  inputCaptureCheck ??= document.createElement('input');
  return inputCaptureCheck.capture != null;
}

let inputCaptureCheck: HTMLInputElement | undefined;

/**
 * Environment = back facing camera
 * User = front facing camera
 *
 * Returns 'user' for desktop and 'environment' for tablets and phones.
 *
 * TODO: Convertible laptops like Microsoft Surface are special cases. They
 * currently always returns 'user', but should return 'environment' when in
 * tablet mode. Unfortunately, no APIs exist today to consistently detect
 * tablet mode.
 */
export function getCameraFacingMode(): 'environment' | 'user' {
  return supportsInputCapture() ? 'environment' : 'user';
}

/**
 * Camera detection functions
 * TODO: check permission by looking at 'label' property on device
 */
let cachedCheckHasCameraResult: boolean | null;
export async function checkHasCamera() {
  if (cachedCheckHasCameraResult != null) {
    return cachedCheckHasCameraResult;
  }

  cachedCheckHasCameraResult = await checkDevicesSafely((devices) => {
    if (!devices) {
      return false;
    }

    return devices.some(isVideoDevice);
  });

  return cachedCheckHasCameraResult;
}

export async function checkIfDevicePermissionGranted(audio = false) {
  return await checkDevicesSafely((devices) => {
    const hasCamera = devices
      .filter(isVideoDevice)
      // If permissions not granted, labels will be undefined
      .some(({ label }) => Boolean(label));

    const hasMic =
      !audio ||
      devices.filter((device) => device.kind.includes('audio')).some(({ label }) => Boolean(label));

    return hasCamera && hasMic;
  });
}

export function loadCameraOptions(): VerifyThunkAction {
  return async (dispatch, getState) => {
    if (getState().ui.cameraOptions != null) {
      return;
    } else {
      const cameraOptions = await getCameraDevices();
      dispatch(setCameraOptions(cameraOptions));
    }
  };
}

async function checkDevicesSafely(checkFunction: (devices: MediaDeviceInfo[]) => boolean) {
  if (!window.navigator || !window.navigator.mediaDevices) {
    // In some cases `window.navigator.mediaDevices` is undefined.
    //
    // Known cases:
    // - Document is loaded insecurely (expired SSL or insecure browser or localhost)
    // - Loaded through SFSafariViewController (iOS in-app browser) https://bugs.webkit.org/show_bug.cgi?id=180551#c5
    //
    // `webrtc-adapter` does not successfully shim over this. It only shims when `window.navigator.mediaDevices` is defined.
    return false;
  }

  try {
    return checkFunction(await window.navigator.mediaDevices.enumerateDevices());
  } catch (err) {
    // Incompatible device
  }

  return false;
}

function isVideoDevice({ kind = '' }) {
  return kind.includes('video');
}

interface WebGLDebugInfo {
  webglSupportedExtensions: string[] | null | undefined;
  webglBufferSize: unknown;
  webgl2SupportedExtensions: string[] | null | undefined;
  webgl2BufferSize: unknown;
}

let webGlDebugInfo: WebGLDebugInfo | undefined;

export function generateWebGLDebugInfo(): WebGLDebugInfo | undefined {
  if (webGlDebugInfo != null) return webGlDebugInfo;

  // Can't create multiple contexts on same canvas unless they're compatible
  const [c1, cleanupC1] = createCanvas();
  const [c2, cleanupC2] = createCanvas();
  const gl = c1.getContext('webgl');
  const gl2 = c2.getContext('webgl2');

  webGlDebugInfo = {
    webglSupportedExtensions: gl?.getSupportedExtensions(),
    webglBufferSize: gl?.getBufferParameter(gl.ARRAY_BUFFER, gl.BUFFER_SIZE),
    webgl2SupportedExtensions: gl2?.getSupportedExtensions(),
    webgl2BufferSize: gl2?.getBufferParameter(gl2.ARRAY_BUFFER, gl2.BUFFER_SIZE),
  };

  cleanupC1();
  cleanupC2();

  return webGlDebugInfo;
}

/**
 * Primitive device checkers
 * TODO (PER-24171): Update to prioritize user agent client hints if browser supports it
 *  Compatibility from MDN: https://developer.mozilla.org/en-US/docs/Web/API/User-Agent_Client_Hints_API#browser_compatibility
 */

export function isMac() {
  const parser = new UAParser(navigator.userAgent);
  return parser.getOS().name === 'Mac OS';
}

export function isIOS() {
  const parser = new UAParser(navigator.userAgent);
  const device = parser.getDevice();
  return device?.model ? /iPhone|iPad/.test(device.model) : false;
}

export function isAndroid() {
  return new UAParser(navigator.userAgent).getOS()?.name === 'Android';
}

export function isWindows() {
  return new UAParser(navigator.userAgent).getOS()?.name === 'Windows';
}

export function isSafari() {
  const { name: browserName } = new UAParser(navigator.userAgent).getBrowser();

  return ['Safari', 'Mobile Safari', 'WebKit'].some((b) => b === browserName);
}

/**
 * Composite device checkers
 */

export function isMacSafari() {
  return isMac() && isSafari();
}

/**
 * Checks if the device is on v16+ of iOS or iPadOS.
 * TODO: revisit if this is still an issue with iOS 20 lolz
 */
export function isIOS16Phone() {
  const version = new UAParser(navigator.userAgent).getOS()?.version;
  return isIOS() && (version ? /^1[6-9]\./.test(version) : false);
}

export function isIOS17Phone() {
  const version = new UAParser(navigator.userAgent).getOS()?.version;
  return isIOS() && (version ? /^1[7-9]\./.test(version) : false);
}

export function isSafari16() {
  const { major: browserMajorVersion } = new UAParser(navigator.userAgent).getBrowser();

  return isSafari() && Number(browserMajorVersion) >= 16;
}

export function isAndroidFacebookBrowser() {
  const parser = new UAParser(navigator.userAgent);
  const { name: browserName } = parser.getBrowser();

  return isAndroid() && browserName === 'Facebook';
}

export function isMobile() {
  return isIOS() || isAndroid();
}

// Doordash sends us a custom user agent so the usual checks for OS/Browser doesn't work,
//  hence we do a bunch of custom checking here
// Snowflake query to see the user agents they are sending: https://app.snowflake.com/ltmvkku/rc70472/w18eUXA8R9mD#query
// Some samples:
//  - "UA-name:iOSDasherWebView; client-version: 2.339.0.207658; device-version: iOS 16.6.1"
//  - "UA-name:AndroidDasherWebView; client-version:7.84.6; device-version:android 33"
//  - "UA-name:iOSDasherWebView (iPhone); client-version: 2.333.0.196444"
//  - "UA-name:iOSCupcakeWebView; client-version: 2.333.0.196444; native-nav-bar"
export function isDoordashUserAgent() {
  return navigator.userAgent?.startsWith('UA-name:');
}

export function isIOSDoordashUserAgent() {
  return navigator.userAgent?.startsWith('UA-name:iOS');
}

export function isAndroidDoordashUserAgent() {
  return navigator.userAgent?.startsWith('UA-name:Android');
}

// looking through the list of uagents, this is not always populated
export function extractDeviceVersionFromDoordashUserAgent() {
  const regex = /device-version:([^;]*)(;|$)/;
  const matchResults = navigator.userAgent?.match(regex);
  if (matchResults == null || matchResults.length < 2) {
    return null;
  }
  return matchResults?.[1].trim();
}

export function extractDeviceVersionNumberFromDoordashUserAgent() {
  const deviceVersion = extractDeviceVersionFromDoordashUserAgent();
  const deviceComponents = deviceVersion?.split(' ');
  // first element should be OS (iOS or android), second element should be the version (e.g. 16.6.1, 33)
  if (deviceComponents?.length !== 2) {
    return null;
  }
  return deviceComponents[1];
}

export function extractClientVersionFromDoordashUserAgent() {
  const regex = /client-version:([^;]*)(;|$)/;
  const matchResults = navigator.userAgent?.match(regex);
  if (matchResults == null || matchResults.length < 2) {
    return null;
  }
  return matchResults?.[1].trim();
}
